/* =============================================================
   BOOKING LABELS
   =============================================================
   Words for the booking_mode, berth_block_reason and
   booking_status enums, plus the money, time and rule phrasing
   every booking screen shares. Client-side for the reason
   vesselTypes.ts gives: the labels are what gets translated.

   `Record<…, string>` is exhaustive, so an enum value added
   without a label fails the build.
   ============================================================= */

import type {
  BerthRate,
  BlockReason,
  Booking,
  BookingMode,
  BookingStatus,
} from './types'

export const BOOKING_MODES: Array<{ code: BookingMode; label: string; hint: string }> = [
  { code: 'instant', label: 'Instant', hint: 'Requests confirm automatically.' },
  { code: 'manual', label: 'Manual confirm', hint: 'The owner accepts or rejects each request.' },
]

const MODE_LABELS: Record<BookingMode, string> = { instant: 'Instant', manual: 'Manual confirm' }
export const bookingModeLabel = (m: BookingMode): string => MODE_LABELS[m] ?? m

const REASON_LABELS: Record<BlockReason, string> = {
  closed_season: 'Closed for the season',
  maintenance: 'Maintenance',
  long_term_tenant: 'Long-term tenant',
  offline_booking: 'Phone / walk-in booking',
}
export const BLOCK_REASONS = (Object.keys(REASON_LABELS) as BlockReason[]).map((code) => ({
  code,
  label: REASON_LABELS[code],
}))
export const blockReasonLabel = (r: BlockReason): string => REASON_LABELS[r] ?? r

/**
 * What a booking IS right now, which is the stored status plus one derived
 * state: a confirmed booking whose departure has passed is completed. The
 * database does not store `completed` on purpose -- see the bookings
 * migration -- so every screen derives it here, the same way.
 */
export type BookingState = BookingStatus | 'completed'

export function bookingState(b: Pick<Booking, 'status' | 'departure'>): BookingState {
  if (b.status === 'confirmed' && new Date(b.departure).getTime() < Date.now()) return 'completed'
  return b.status
}

const STATE_LABELS: Record<BookingState, string> = {
  requested: 'Awaiting owner',
  confirmed: 'Confirmed',
  completed: 'Completed',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
}
const STATE_TONES: Record<BookingState, 'neutral' | 'accent' | 'success' | 'warning' | 'danger'> = {
  requested: 'warning',
  confirmed: 'success',
  completed: 'neutral',
  rejected: 'danger',
  cancelled: 'neutral',
}
export const BOOKING_STATES = Object.keys(STATE_LABELS) as BookingState[]
export const bookingStateLabel = (s: BookingState): string => STATE_LABELS[s]
export const bookingStateTone = (s: BookingState) => STATE_TONES[s]

/* --- money, time ---------------------------------------------------------- */

const eur = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'EUR' })
export const formatEur = (n: number): string => eur.format(n)

const dt = new Intl.DateTimeFormat('en-GB', { dateStyle: 'medium', timeStyle: 'short' })
export const formatDateTime = (iso: string): string => dt.format(new Date(iso))

/** "28 h", "1 d 4 h" -- how long a stay or block lasts, at a glance. */
export function formatDuration(fromIso: string, toIso: string): string {
  const hours = (new Date(toIso).getTime() - new Date(fromIso).getTime()) / 3_600_000
  if (hours < 48) return `${+hours.toFixed(1)} h`
  const days = Math.floor(hours / 24)
  const rest = Math.round(hours - days * 24)
  return rest ? `${days} d ${rest} h` : `${days} d`
}

/* --- rules ---------------------------------------------------------------- */

/** "580 € per 24 h", "100 € flat" -- the price half of a rule. */
export function ratePriceText(r: Pick<BerthRate, 'price_eur' | 'per_hours'>): string {
  const price = formatEur(r.price_eur)
  return r.per_hours === null ? `${price} flat per stay` : `${price} per ${r.per_hours} h`
}

/** "Ships up to 110 m · any stay" -- the condition half. */
export function rateConditionText(
  r: Pick<BerthRate, 'max_vessel_length_m' | 'max_stay_hours'>,
): string {
  const ships = r.max_vessel_length_m === null ? 'Any ship' : `Ships up to ${r.max_vessel_length_m} m`
  const stays = r.max_stay_hours === null ? 'any stay' : `stays up to ${r.max_stay_hours} h`
  return `${ships} · ${stays}`
}

/* --- datetime-local ------------------------------------------------------- */

/** An ISO instant as the value a <input type="datetime-local"> wants, in local time. */
export function toLocalInput(iso: string): string {
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/** A datetime-local value, read as local time, as an ISO instant. '' → null. */
export function fromLocalInput(value: string): string | null {
  if (!value) return null
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? null : d.toISOString()
}
